const SAFARI_NETWORK_LOAD_MESSAGES = new Set([
  "load failed",
  "failed to fetch",
  "networkerror when attempting to fetch resource.",
  "networkerror when attempting to fetch resource",
]);

type PostHogExceptionItem = {
  type?: unknown;
  value?: unknown;
};

export type PostHogCaptureLike = {
  event?: unknown;
  properties?: {
    $exception_types?: unknown;
    $exception_values?: unknown;
    $exception_list?: unknown;
  };
} | null;

function readErrorName(error: object): string {
  return "name" in error && typeof error.name === "string" ? error.name : "";
}

function readErrorMessage(error: object): string {
  return "message" in error && typeof error.message === "string"
    ? error.message
    : "";
}

function isNetworkLoadTypeErrorName(name: string, error: unknown): boolean {
  return (
    name === "TypeError" ||
    error instanceof TypeError ||
    name === "NetworkError"
  );
}

function isBareNetworkLoadMessage(message: string): boolean {
  return SAFARI_NETWORK_LOAD_MESSAGES.has(message.trim().toLowerCase());
}

export function isSafariNetworkLoadError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }

  const name = readErrorName(error);
  const message = readErrorMessage(error);
  return (
    isNetworkLoadTypeErrorName(name, error) && isBareNetworkLoadMessage(message)
  );
}

export async function refreshAppRouterSafely(
  refresh: () => void | Promise<void>,
): Promise<void> {
  try {
    await refresh();
  } catch (error) {
    if (isSafariNetworkLoadError(error)) {
      return;
    }
    throw error;
  }
}

function firstString(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  if (Array.isArray(value) && typeof value[0] === "string") {
    return value[0];
  }
  return "";
}

function exceptionFromPostHogCapture(
  event: PostHogCaptureLike,
): { name: string; message: string } | null {
  if (!event || event.event !== "$exception") {
    return null;
  }

  const properties = event.properties;
  const list = properties?.$exception_list;
  if (Array.isArray(list) && list[0] && typeof list[0] === "object") {
    const item = list[0] as PostHogExceptionItem;
    return {
      name: typeof item.type === "string" ? item.type : "",
      message: typeof item.value === "string" ? item.value : "",
    };
  }

  return {
    name: firstString(properties?.$exception_types),
    message: firstString(properties?.$exception_values),
  };
}

export function shouldDropPostHogSafariNetworkLoadException(
  event: PostHogCaptureLike,
): boolean {
  const exception = exceptionFromPostHogCapture(event);
  if (!exception) {
    return false;
  }
  return isSafariNetworkLoadError(exception);
}

export function dropPostHogSafariNetworkLoadException<T extends PostHogCaptureLike>(
  event: T,
): T | null {
  if (shouldDropPostHogSafariNetworkLoadException(event)) {
    return null;
  }
  return event;
}
